import React, { useState, useContext, createContext, useCallback } from "react";
import { ThemeContext } from "@unum/theme";
import Heading, { Level } from "@unum/heading";
import styles from "./Card.module.scss";
import classnames from "classnames";

type HeaderProps = {
  cardIcon?: SVGImport;
  children: React.ReactNode;
  className?: string;
  level?: number;
  overline?: string;
};

const Header = ({
  children,
  cardIcon: CardIcon,
  className,
  overline,
  level = 5
}: HeaderProps) => {
  const itemKey = useContext(CardContext);
  const { activeCard, setActiveCard } = useContext(AccordionContext);
  const { accentColour } = useContext(ThemeContext);

  return (
    <div
      className={styles["header"]}
      onClick={() => {
        if (setActiveCard) {
          setActiveCard(itemKey !== activeCard ? itemKey : null);
        }
      }}
    >
      {CardIcon && (
        <div style={{ color: accentColour }}>
          <CardIcon className={styles["icon"]} />
        </div>
      )}
      {overline && <p className={styles["overline"]}>{overline}</p>}
      <Heading
        level={level as Level}
        className={classnames(styles["title"], className)}
      >
        {children}
      </Heading>
    </div>
  );
};

const Body = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={classnames(styles["content"], className)}>{children}</div>
  );
};

const Footer = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={classnames(styles["footer"], className)}>{children}</div>
  );
};

const CardContext = createContext<number | null>(null);

type Props = {
  children: React.ReactNode;
  itemKey?: number;
  backgroundColour?: "transparent" | "grey" | "white";
  align?: "left" | "center";
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

const Card = ({
  children,
  itemKey,
  backgroundColour = "grey",
  align = "center",
  className,
  ...rest
}: Props) => {
  const { activeCard } = useContext(AccordionContext);

  if (activeCard !== undefined && itemKey === undefined) {
    throw new Error("Card: You have to define itemKey for Card in Accordion.");
  }

  return (
    <div
      className={classnames(styles["Card"], className, {
        [styles["Card--active"]]:
          activeCard !== undefined && activeCard === itemKey,
        [styles["Card--grouped"]]: activeCard !== undefined,
        [styles[`Card--${backgroundColour}`]]: backgroundColour !== "grey",
        [styles[`Card--${align}`]]: align !== "center"
      })}
      {...rest}
    >
      <CardContext.Provider value={itemKey || null}>
        {children}
      </CardContext.Provider>
    </div>
  );
};

Card.Header = Header;
Card.Body = Body;
Card.Footer = Footer;

export default Card;

type AccordionProps = {
  children: React.ReactNode;
};

type Context = {
  activeCard?: number | null;
  setActiveCard?: (card: number | null) => void;
};

const AccordionContext = createContext<Context>({});

export const CardAccordion = ({ children }: AccordionProps) => {
  const [activeCard, setActiveCard] = useState<Context["activeCard"]>(null);
  const handleCardClick = useCallback((card: number | null) => {
    setActiveCard(card);
  }, []);

  return (
    <AccordionContext.Provider
      value={{ activeCard, setActiveCard: handleCardClick }}
    >
      <div className={styles["CardAccordion"]}>{children}</div>
    </AccordionContext.Provider>
  );
};
